(() => {
  const elementName = 'how-to-wear-care-section';
  if (customElements.get(elementName)) return;

  class MobileCarousel {
    constructor(group, mobileMedia) {
      this.group = group;
      this.mobile = mobileMedia;
      this.viewport = group.querySelector('[data-carousel-viewport]');
      this.track = group.querySelector('[data-carousel-track]');
      this.slides = Array.from(group.querySelectorAll('[data-carousel-slide]'));
      this.dots = Array.from(group.querySelectorAll('[data-carousel-dot]'));
      this.previous = group.querySelector('[data-carousel-previous]');
      this.next = group.querySelector('[data-carousel-next]');
      this.autoplay = group.dataset.autoplay === 'true';
      this.loop = group.dataset.loop === 'true';
      this.pauseOnHover = group.dataset.pauseOnHover === 'true';
      this.delay = Math.max(2000, Number(group.dataset.delay) || 5000);
      this.index = 0;
      this.timer = null;
      this.pointerStart = null;
      if (!this.viewport || !this.track || !this.slides.length) return;

      this.onPrevious = () => this.move(-1, true);
      this.onNext = () => this.move(1, true);
      this.onKeydown = (event) => {
        if (!this.mobile.matches) return;
        if (event.key === 'ArrowLeft') this.move(-1, true);
        if (event.key === 'ArrowRight') this.move(1, true);
      };
      this.onPointerDown = (event) => {
        if (!this.mobile.matches || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.pointerStart = event.clientX;
        this.track.classList.add('is-dragging');
        this.stop();
      };
      this.onPointerUp = (event) => {
        if (this.pointerStart === null) return;
        const distance = event.clientX - this.pointerStart;
        this.pointerStart = null;
        this.track.classList.remove('is-dragging');
        if (Math.abs(distance) > 42) this.move(distance < 0 ? 1 : -1, true);
        else this.goTo(this.index);
        this.start();
      };
      this.onPointerCancel = () => {
        this.pointerStart = null;
        this.track.classList.remove('is-dragging');
        this.goTo(this.index);
        this.start();
      };

      this.previous?.addEventListener('click', this.onPrevious);
      this.next?.addEventListener('click', this.onNext);
      this.viewport.addEventListener('keydown', this.onKeydown);
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerCancel);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => {
        this.goTo(index);
        this.restart();
      }));

      if (this.pauseOnHover) {
        this.group.addEventListener('mouseenter', () => this.stop());
        this.group.addEventListener('mouseleave', () => this.start());
        this.group.addEventListener('focusin', () => this.stop());
        this.group.addEventListener('focusout', () => this.start());
      }
      this.refresh();
    }

    refresh() {
      if (!this.track) return;
      if (!this.mobile.matches) {
        this.stop();
        this.track.style.transform = '';
        return;
      }
      this.index = Math.min(this.index, this.slides.length - 1);
      this.goTo(this.index, false);
      this.start();
    }

    move(direction, userInitiated = false) {
      if (!this.mobile.matches || this.slides.length < 2) return;
      let nextIndex = this.index + direction;
      if (this.loop) nextIndex = (nextIndex + this.slides.length) % this.slides.length;
      else nextIndex = Math.max(0, Math.min(nextIndex, this.slides.length - 1));
      this.goTo(nextIndex);
      if (userInitiated) this.restart();
    }

    goTo(index, animate = true) {
      if (!this.mobile.matches || !this.slides[index]) return;
      this.index = index;
      const slide = this.slides[index];
      const center = slide.offsetLeft + (slide.offsetWidth / 2);
      const translate = (this.viewport.clientWidth / 2) - center;
      if (!animate) this.track.style.transitionDuration = '0ms';
      this.track.style.transform = `translate3d(${translate}px, 0, 0)`;
      if (!animate) {
        this.track.getBoundingClientRect();
        this.track.style.transitionDuration = '';
      }
      this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false'));
      if (!this.loop) {
        if (this.previous) this.previous.disabled = index === 0;
        if (this.next) this.next.disabled = index === this.slides.length - 1;
      }
    }

    showBlock(block) {
      const slide = block.closest('[data-carousel-slide]');
      const index = this.slides.indexOf(slide);
      if (index > -1) this.goTo(index);
    }

    start() {
      this.stop();
      if (!this.mobile.matches || !this.autoplay || this.slides.length < 2 || document.hidden) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = window.setInterval(() => this.move(1), this.delay);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() { this.stop(); this.start(); }

    destroy() {
      this.stop();
      this.previous?.removeEventListener('click', this.onPrevious);
      this.next?.removeEventListener('click', this.onNext);
      this.viewport?.removeEventListener('keydown', this.onKeydown);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerCancel);
    }
  }

  class HowToWearCareSection extends HTMLElement {
    connectedCallback() {
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.carousels = Array.from(this.querySelectorAll('[data-carousel-group]')).map((group) => new MobileCarousel(group, this.mobile));
      this.resizeFrame = null;
      this.onResize = () => {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(() => this.carousels.forEach((carousel) => carousel.refresh()));
      };
      this.onVisibility = () => this.carousels.forEach((carousel) => document.hidden ? carousel.stop() : carousel.start());
      this.onBlockSelect = (event) => this.carousels.forEach((carousel) => {
        if (carousel.group.contains(event.target)) {
          carousel.showBlock(event.target);
          carousel.stop();
        }
      });
      this.onBlockDeselect = () => this.carousels.forEach((carousel) => carousel.start());
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    disconnectedCallback() {
      this.carousels?.forEach((carousel) => carousel.destroy());
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  customElements.define(elementName, HowToWearCareSection);
})();

